import { useEffect, useState, useContext } from "react";
import {
    Box, Card, Grid,
    Typography, Stack
} from '@mui/material';
import "../styles/connect-timing-main-card.scss";
import CustomSwitch from '../../../components/CustomSwitch';
import advancedSetttingServiceSerive, { NotificationChannel, NotificationSetting } from "../../../services/http/advanced-settings/advanced-settings.services";
import { getAttributeFromToken } from "../../../hooks/use-auth";
import { AppContext } from "../../../contexts/app.context";
import SettingSkeleton from "./settings-skeleton";

type NotificationSettingsProps = {
}

type ChannelField = 'sms' | 'whatsApp';

const CHANNEL_FIELD: Record<NotificationChannel, ChannelField> = {
    sms: 'sms',
    whatsapp: 'whatsApp'
};

export default function NotificationSettings(props: NotificationSettingsProps) {
    const appContext: any = useContext(AppContext);
    const [settings, setSettings] = useState<NotificationSetting[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        getNotificationSettings();
        return () => { }
        // eslint-disable-next-line
    }, [])

    /**
     * Get the SMS / WhatsApp preferences for every notification event
     */
    const getNotificationSettings = async (): Promise<void> => {
        try {
            const partyId = getAttributeFromToken('partyId');
            const resp = await advancedSetttingServiceSerive.getNotificationSettings(partyId);

            setSettings(resp || []);
        } catch (error) {
            console.error("Error in getNotificationSettings", error);
            appContext.toast({ open: true, message: "Could not load notification settings.", severity: "error" });
        } finally {
            setIsLoading(false);
        }
    }

    /**
     * Toggle a single channel for a single notification event. The switch is
     * updated optimistically and reverted if the save fails.
     */
    const updateNotificationSetting = async (
        e: React.ChangeEvent<HTMLInputElement>,
        item: NotificationSetting,
        channel: NotificationChannel
    ): Promise<void> => {
        const enabled = e.target.checked;
        const field = CHANNEL_FIELD[channel];

        setSettings((prevState) => prevState.map((setting) => (
            setting.eventKey === item.eventKey ? { ...setting, [field]: enabled } : setting
        )));

        try {
            const partyId = getAttributeFromToken('partyId');
            const { message } = await advancedSetttingServiceSerive.updateNotificationSetting({
                fromPartyId: partyId,
                eventKey: item.eventKey,
                channel,
                enabled
            });

            appContext.toast({ open: true, message, severity: "success" });
            await getNotificationSettings();
        } catch (error) {
            console.error("Error in updateNotificationSetting", error);

            setSettings((prevState) => prevState.map((setting) => (
                setting.eventKey === item.eventKey ? { ...setting, [field]: !enabled } : setting
            )));

            appContext.toast({ open: true, message: "Could not update the notification preference. Please try again.", severity: "error" });
        }
    }

    if (isLoading) {
        return <SettingSkeleton />
    }

    return (
        <Grid container className="connect-timing-card-root"
            sx={{
                '.MuiPaper-root': {
                    width: "100%"
                }
            }}>
            <Grid item xs={12} md={12}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    Switch off a channel to stop that message being sent to your patients. Turning
                    both switches off means no message is sent for that event at all.
                </Typography>
            </Grid>
            {
                settings.map((item: NotificationSetting) => {
                    return (
                        <Grid item xs={12} md={12} key={item.eventKey}>
                            <Card sx={{ padding: '0px', mb: 1 }}>
                                <Grid container spacing={2} sx={{ pt: '0px !important' }}>
                                    <Grid item xs={12} md={12}>
                                        <Box sx={{
                                            padding: '1rem 0.5rem 1rem 1rem !important'
                                        }}>
                                            <Typography variant="h6" component="div">
                                                {item.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                                                {item.description}
                                            </Typography>
                                            <Stack
                                                direction={"row"}
                                                alignItems={"center"}
                                                flexWrap={"wrap"}
                                                sx={{ columnGap: 3, rowGap: 1 }}
                                            >
                                                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                                                    <Typography variant="subtitle1" component="span">
                                                        SMS
                                                    </Typography>
                                                    <CustomSwitch
                                                        checked={item.sms}
                                                        inputProps={{ 'aria-label': `SMS for ${item.label}` }}
                                                        onChange={(e) => updateNotificationSetting(e, item, 'sms')}
                                                    />
                                                </Stack>
                                                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                                                    <Typography variant="subtitle1" component="span">
                                                        WhatsApp
                                                    </Typography>
                                                    <CustomSwitch
                                                        checked={item.whatsApp}
                                                        inputProps={{ 'aria-label': `WhatsApp for ${item.label}` }}
                                                        onChange={(e) => updateNotificationSetting(e, item, 'whatsapp')}
                                                    />
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    )
                })
            }
        </Grid>
    )
}
